import React from 'react';
import { MapPin, Star } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar.jsx';
import { Badge } from '../ui/badge.jsx';
import { Button } from '../ui/button.jsx';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.jsx';
import { formatDateWithTZ, formatTimeWithTZ } from '../../lib/utils';

export const BarbershopCard = React.memo(({
  barbershop,
  upcomingBooking,
  recentCancelledBookings,
  setSelectedBarbershop,
  setSelectedUpcomingBooking,
  getBarbershopAvatarUrl,
  formatClp,
  openBookingModal,
  openMapForBarbershop
}) => {
  const hasUpcoming = Boolean(upcomingBooking);
  const bookingDate = upcomingBooking?.date || upcomingBooking?.created_at;
  const bookingDateLabel = bookingDate
    ? formatDateWithTZ(bookingDate, { day: '2-digit', month: 'long', year: 'numeric' })
    : null;
  const bookingTimeLabel = bookingDate
    ? formatTimeWithTZ(bookingDate, { hour: '2-digit', minute: '2-digit' })
    : null;
  const cancellationEntry = Object.values(recentCancelledBookings).find(
    (entry) => entry?.barbershopId === barbershop.id && Date.now() - entry.timestamp < 15 * 60 * 1000
  );

  return (
    <Card
      className={`${hasUpcoming ? 'bg-gray-800 border-amber-500 ring-1 ring-amber-400/40' : 'bg-gray-900 border-gray-700'} text-white hover:bg-gray-800 transition-colors cursor-pointer`}
      onClick={() => setSelectedBarbershop(barbershop)}
    >
      <CardHeader className="pb-2 sm:pb-3 p-3 sm:p-6">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <Avatar className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0">
              <AvatarImage
                src={getBarbershopAvatarUrl(barbershop)}
                alt={barbershop.name}
                loading="lazy"
                decoding="async"
              />
              <AvatarFallback className="text-sm">{barbershop.name[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <CardTitle className="text-sm sm:text-lg truncate">{barbershop.name}</CardTitle>
              <div className="flex items-center gap-1 mt-0.5 sm:mt-1">
                <Star className="w-3 h-3 sm:w-4 sm:h-4 fill-amber-400 text-amber-400 flex-shrink-0" />
                <span className="text-xs sm:text-sm whitespace-nowrap">{barbershop.rating || 0} ({barbershop.reviews_count || 0})</span>
              </div>
            </div>
          </div>
          <Badge
            className={`${
              hasUpcoming
                ? 'bg-amber-500 text-black'
                : barbershop.available
                ? 'bg-green-600'
                : 'bg-gray-600'
            } text-xs whitespace-nowrap`}
          >
            {hasUpcoming ? 'Alta demanda' : barbershop.available ? 'Disponible' : 'Ocupado'}
          </Badge>
        </div>
        {barbershop.weekly_rank && (
          <div className="mt-2 text-xs text-amber-300">
            Top semanal: #{barbershop.weekly_rank}
          </div>
        )}
        {(barbershop.accepting_bookings === false || barbershop.accepting_quick_cuts === false) && (
          <div className="mt-2 text-xs text-red-200">
            {barbershop.accepting_bookings === false && barbershop.accepting_quick_cuts === false
              ? 'Agenda llena'
              : barbershop.accepting_bookings === false
              ? 'Reservas pausadas'
              : 'Cortes rápidos pausados'}
          </div>
        )}
        {hasUpcoming && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setSelectedUpcomingBooking({
                ...upcomingBooking,
                barbershop: upcomingBooking?.barbershop || barbershop
              });
            }}
            className="mt-3 w-full text-left rounded-lg border border-amber-500/60 bg-amber-500/10 px-3 py-2 text-xs text-amber-200 hover:bg-amber-500/20 transition-colors"
          >
            <div className="font-semibold uppercase tracking-wide">Reserva en curso</div>
            <div className="mt-1 text-amber-100">
              {bookingDateLabel} • {bookingTimeLabel}
            </div>
            <div className="mt-1 text-[11px] text-amber-300">Toca para ver detalles</div>
          </button>
        )}
        {!hasUpcoming && cancellationEntry && (
          <div className="mt-3 rounded-lg border border-red-500/60 bg-red-500/10 px-3 py-2 text-xs text-red-200">
            <div className="font-semibold uppercase tracking-wide">Reserva cancelada</div>
            <div className="mt-1 text-red-100">
              Disponible nuevamente en breve
            </div>
          </div>
        )}
      </CardHeader>
      <CardContent className="p-3 sm:p-6 pt-0">
        <div className="space-y-2">
          <button
            type="button"
            className="text-gray-300 text-xs sm:text-sm flex items-start gap-1 hover:text-amber-300 transition-colors min-w-0"
            onClick={(e) => {
              e.stopPropagation();
              openMapForBarbershop?.(barbershop);
            }}
          >
            <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
            <span className="break-words text-left leading-snug">
              {barbershop.address}
            </span>
          </button>
          {barbershop.services && barbershop.services.length > 0 && (
            <div>
              <p className="text-amber-400 font-medium text-sm sm:text-base">
                {formatClp(barbershop.services[0].price)} - {formatClp(barbershop.services[barbershop.services.length - 1].price)}
              </p>
              <div className="flex gap-1 sm:gap-2 mt-2 flex-wrap">
                {barbershop.services.slice(0, 2).map((service, index) => (
                  <Badge
                    key={index}
                    variant="outline"
                    className="text-xs border-amber-500/60 text-amber-300 bg-amber-500/10"
                  >
                    {service.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-2 mt-3">
            <Button
              size="sm"
              className="flex-1 bg-amber-500 hover:bg-amber-700 text-xs sm:text-sm py-1.5 sm:py-2"
              onClick={(e) => {
                e.stopPropagation();
                openBookingModal(barbershop);
              }}
              disabled={barbershop.accepting_bookings === false}
            >
              {barbershop.accepting_bookings === false ? 'Reservas pausadas' : 'Reservar'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});
